import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createAppointment,
  listAppointmentFilterOptions,
  listAppointments,
} from "./appointments.api";
import { AppointmentFilterOptions, AppointmentJoin } from "@/repositories/appointments.repository";
import { ReadAllResponse } from "@/interfaces";
import { appointment_status } from "@/models/appointment.model";
import { CreateAppointmentPayload } from "@/app/pmc/appointments/appointments.entity.ts";
import { cancelAppointment } from "@/services/appointment-cancellation.service";
import { rescheduleAppointment } from "@/services/appointment-reschedule.service";
import { sendBack } from "@/services/appointment-go-back.service";

interface FilterState {
  status: string;
  serviceParent: string;
  vendor: string;
  assignee: string;
  search: string;
  dateFrom: string;
  dateTo: string;
  sortKey: string;
  sortOrder: "asc" | "desc";
}

interface PaginationState {
  page: number;
  pageSize: number;
}

interface AppointmentsState {
  list: AppointmentJoin[];
  loading: boolean;
  error: string | null;
  total: number;
  filters: FilterState;
  pagination: PaginationState;
  options: AppointmentFilterOptions;
  sheetOpen: boolean;
  lastCreated: CreateAppointmentPayload | null;
}

const initialState: AppointmentsState = {
  list: [],
  loading: false,
  error: null,
  total: 0,
  filters: {
    status: "",
    serviceParent: "",
    vendor: "",
    assignee: "",
    search: "",
    dateFrom: "",
    dateTo: "",
    sortKey: "created_at",
    sortOrder: "desc",
  },
  pagination: { page: 1, pageSize: 50 },
  options: { serviceParents: [], vendorNames: [], assigneeNames: [] },
  sheetOpen: false,
  lastCreated: null,
};

export const fetchAppointments = createAsyncThunk(
  "appointments/fetch",
  async (searchOverride?: string, { getState } = {} as any) => {
    const s = (getState() as { appointments: AppointmentsState }).appointments;
    const { page, pageSize } = s.pagination;
    const { status, serviceParent, vendor, assignee, search, dateFrom, dateTo, sortKey, sortOrder } = s.filters;

    return await listAppointments(
      page,
      pageSize,
      {
        status: status ? (status as appointment_status) : undefined,
        serviceParent: serviceParent || undefined,
        vendor: vendor || undefined,
        assignee: assignee || undefined,
        search: searchOverride !== undefined ? searchOverride : (search || undefined),
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      },
      sortKey,
      sortOrder
    );
  }
);

export const fetchAppointmentFilterOptions = createAsyncThunk(
  "appointments/fetchFilterOptions",
  async () => await listAppointmentFilterOptions()
);

export const saveAppointment = createAsyncThunk("appointments/create", async (payload: CreateAppointmentPayload) => {
  return await createAppointment(payload);
});

export const goBack = createAsyncThunk("appointments/goBack", async (args: { id: string; date: string }) =>
  sendBack(args.id, args.date)
);

export const cancel = createAsyncThunk("appointments/cancel", async (args: { id: string }) =>
  cancelAppointment(args.id)
);

export const reschedule = createAsyncThunk("appointments/reschedule", async (args: { id: string; date: string }) =>
  rescheduleAppointment(args.id, args.date)
);

const slice = createSlice({
  name: "appointments",
  initialState,
  reducers: {
    /* filter setters */
    setStatus(s, a: PayloadAction<string>) {
      s.filters.status = a.payload;
      s.pagination.page = 1;
    },
    setServiceParent(s, a: PayloadAction<string>) {
      s.filters.serviceParent = a.payload;
      s.pagination.page = 1;
    },
    setVendor(s, a: PayloadAction<string>) {
      s.filters.vendor = a.payload;
      s.pagination.page = 1;
    },
    setAssignee(s, a: PayloadAction<string>) {
      s.filters.assignee = a.payload;
      s.pagination.page = 1;
    },
    setSearch(s, a: PayloadAction<string>) {
      s.filters.search = a.payload;
      s.pagination.page = 1;
    },
    setDateFrom(s, a: PayloadAction<string>) {
      s.filters.dateFrom = a.payload;
      s.pagination.page = 1;
    },
    setDateTo(s, a: PayloadAction<string>) {
      s.filters.dateTo = a.payload;
      s.pagination.page = 1;
    },

    /* pagination */
    setPage(s, a: PayloadAction<number>) {
      s.pagination.page = a.payload;
    },
    setPageSize(s, a: PayloadAction<number>) {
      s.pagination.pageSize = a.payload;
      s.pagination.page = 1;
    },

    /* sorting */
    setSortKey(s, a: PayloadAction<string>) {
      if (s.filters.sortKey === a.payload) {
        s.filters.sortOrder = s.filters.sortOrder === "asc" ? "desc" : "asc";
      } else {
        s.filters.sortKey = a.payload;
        s.filters.sortOrder = "asc";
      }
      s.pagination.page = 1;
    },

    openSheet(s) {
      s.sheetOpen = true;
    },
    closeSheet(s) {
      s.sheetOpen = false;
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchAppointments.pending, (s) => {
      s.loading = true;
    });
    b.addCase(fetchAppointments.fulfilled, (s, a: PayloadAction<ReadAllResponse<AppointmentJoin>>) => {
      s.loading = false;
      s.list = a.payload.data;
      s.total = a.payload.count ?? 0;
    });
    b.addCase(fetchAppointments.rejected, (s, a) => {
      s.loading = false;
      s.error = a.error.message || "Failed to fetch appointments.";
    });

    b.addCase(fetchAppointmentFilterOptions.fulfilled, (s, a: PayloadAction<AppointmentFilterOptions>) => {
      s.options = a.payload;
    });

    b.addCase(saveAppointment.fulfilled, (s, a) => {
      s.list.unshift(a.payload);
      s.total += 1;
      s.lastCreated = {
        property: a.payload.property.id,
        unit: a.payload.property.unit_number,
        vendor: a.payload.vendor.id,
        service: a.payload.service.id,
        date: a.payload.date,
        work_order_notes: a.payload.work_order_notes,
      };
    });

    b.addCase(goBack.fulfilled, (s, a) => {
      const idx = s.list.findIndex((r) => r.id === a.payload.id);
      if (idx >= 0) s.list[idx] = a.payload;
    });
    b.addCase(cancel.fulfilled, (s, a) => {
      const idx = s.list.findIndex((r) => r.id === a.payload.id);
      if (idx >= 0) s.list[idx] = a.payload;
    });
    b.addCase(reschedule.fulfilled, (s, a) => {
      const idx = s.list.findIndex((r) => r.id === a.payload.id);
      if (idx >= 0) s.list[idx] = a.payload;
    });
  },
});

export const {
  setStatus,
  setServiceParent,
  setVendor,
  setAssignee,
  setSearch,
  setDateFrom,
  setDateTo,
  setPage,
  setPageSize,
  setSortKey,
  openSheet,
  closeSheet,
} = slice.actions;

export default slice.reducer;
